import React from 'react';
import { useNavigate } from 'react-router-dom';

// Import images (you'll add these to src/assets/)
import executiveTalentImg from '../assets/executive-talent.jpg';
import aiAutomationImg from '../assets/ai-automation.jpg';
import digitalAgencyImg from '../assets/digital-agency.jpg';

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: 1,
      icon: (
        <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl flex items-center justify-center text-2xl text-white mb-6">
          ♛
        </div>
      ),
      image: executiveTalentImg,
      title: "Executive Talent Solutions",
      subtitle: "The Executive Talent Your Organization Needs to Succeed",
      description: "We provide access to a vetted network of top-tier executives and strategic consultants to fill critical leadership gaps and drive your business objectives forward.",
      features: [],
      linkText: "Find Your Leader",
      linkUrl: "/executive-talent"
    },
    {
      id: 2,
      icon: (
        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center text-2xl text-white mb-6">
          🧠
        </div>
      ),
      image: aiAutomationImg,
      title: "AI & Digital Development",
      subtitle: "Intelligently Powerful AI & Automation",
      tagline: "Empower. Excel. Limitlessly Scalable.",
      description: "AdroitOneAI builds custom AI chatbots, agentic AI systems, and workflow automation. Using n8n, RAG-powered AI, and custom integrations, we automate processes across industries.",
      features: [
        "AI Chatbots & Agentic Systems",
        "Workflow & Process Automation",
        "RAG-Powered AI Integrations",
        "Full-Stack Web & App Development"
      ],
      linkText: "Automate Your Workflow",
      linkUrl: "/ai-automation"
    },
    {
      id: 3,
      icon: (
        <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-900 rounded-2xl flex items-center justify-center text-2xl text-white mb-6">
          🎨
        </div>
      ),
      image: digitalAgencyImg,
      title: "Digital Agency",
      subtitle: "New York's Most-Trusted Digital Agency",
      description: "We offer high-quality, affordable websites for small businesses that drive results. Your journey with us always starts with a FREE, no-obligation demo to visualize your success.",
      features: [
        "High-Quality, Affordable Websites",
        "Modern Design & Development",
        "Tailored for Small Businesses"
      ],
      linkText: "Get Your Free Demo",
      linkUrl: "/digital-agency"
    }
  ];

  const handleServiceClick = (url) => {
    navigate(url);
  };

  return (
    <section id="services" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
          Our Core Services
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {services.map((service) => (
            <div 
              key={service.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100 flex flex-col h-full"
            >
              {/* Image Section */}
              <div className="h-48 bg-gradient-to-br from-blue-600 to-cyan-500 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/80 to-cyan-5/80 flex items-center justify-center text-white text-lg font-semibold">
                  {/* Replace this with actual image when available */}
                  <div className="text-center p-4">
                    <div className="text-4xl mb-2">{service.icon.props.children}</div>
                    <div>{service.title}</div>
                  </div>
                </div>
                {/* Actual image (commented out until you add images) */}
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Gradient Top Bar */}
              <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
              
              {/* Content Section - flex-grow makes this section take available space */}
              <div className="p-6 flex-grow flex flex-col">
                {/* Icon */}
                <div className="flex justify-center -mt-12 mb-4">
                  <div className="relative">
                    {service.icon}
                  </div>
                </div>
                
                {/* Content */}
                <div className="text-center flex-grow">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  
                  <h4 className="text-md font-semibold text-blue-600 mb-3 leading-tight">
                    {service.subtitle}
                  </h4>
                  
                  {service.tagline && (
                    <p className="text-sm text-cyan-600 mb-4 font-medium italic">
                      {service.tagline}
                    </p>
                  )}
                  
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  {service.features.length > 0 && (
                    <ul className="mb-6 text-left space-y-2">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                
                {/* Link - positioned at bottom */}
                <div className="mt-auto pt-4">
                  <button 
                    onClick={() => handleServiceClick(service.linkUrl)}
                    className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg text-sm"
                  >
                    {service.linkText}
                    <svg className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;